import React, { useEffect, useState } from "react";
import {
  getCandidateQuestionnaireQuestions,
  getCandidateQuestionnaireQuestionOptions,
  getCandidateQuestionnaireBase,
} from "../../services/candidateQuestionnaireService";
import { SimpleClient } from "../../interfaces/client";
import { CandidateQuestionnaire } from "../../interfaces/candidateQuestionnaire";
import { QuestionOption } from "../../interfaces/questionOption";
import { saveCandidateScreeningQuestionnaire } from "../../services/orderFormService";
import AuthorizationForm, {
  Authorization,
} from "../form-components/AuthorizationForm";
import { Order } from "../../interfaces/order";
import { useNavigate } from "react-router-dom";
import EditBaseText from "./EditBaseText";
import EditQuestion from "./EditQuestion";
import AddQuestion from "./AddQuestion";
import { getSimpleClient } from "../../services/clientService";
import { ReferenceQuestionnaire } from "../../interfaces/referenceQuestionnaire";
import { isValidDate } from "../../services/helpers";
import ToolTip from "../component-tools/ToolTip";
import { getAdditionalForm } from "../../services/additionalFormService";
import { HttpService } from "../../services/httpService";
import { AdditionalForm } from "../../interfaces/additionalForm";

interface CandidateFormProps {
  order?: Order;
  clientID: number;
  orderFormID: number;
  isReadOnly?: boolean; //admin, client
  isEditing: boolean; //admin
  fiveYear: boolean;
}

const CandidateForm: React.FC<CandidateFormProps> = ({
  order,
  clientID,
  orderFormID,
  isReadOnly,
  isEditing,
  fiveYear,
}) => {
  const navigate = useNavigate();
  const [client, setClient] = useState<SimpleClient | null>(null);
  const [questionsWithOptions, setQuestionsWithOptions] = useState<
    Map<number, { questionText: string; options: QuestionOption[] }>
  >(new Map());
  const [candidateQuestionnaire, setCandidateQuestionnaire] =
    useState<CandidateQuestionnaire>();
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});
  const [authorization, setAuthorization] = useState<Authorization>({
    checked: false,
    dated: new Date(),
    aCity: "",
    aState: "",
    fullName: "",
  });
  const [comments, setComments] = useState<string>("");
  const [showEditBaseText, setShowEditBaseText] = useState(false);
  const [editingBaseText, setEditingBaseText] = useState<{
    key: string;
    text: string;
  } | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [isHovered, setIsHovered] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<{
    id: number;
    text: string;
    type: "question" | "option";
  } | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [pageDecorationUrl, setPageDecorationUrl] = useState<string | null>(
    null,
  );
  const [pageDecorationForm, setPageDecorationForm] =
    useState<AdditionalForm | null>(null);

  useEffect(() => {
    const loadClientAndQuestions = async () => {
      try {
        const clientResponse = await getSimpleClient(clientID);
        setClient(clientResponse);

        const candidateQuestionnaireBase = await getCandidateQuestionnaireBase(
          clientID,
          fiveYear,
        );

        setCandidateQuestionnaire(candidateQuestionnaireBase);

        if (candidateQuestionnaireBase.candidateQuestionnaireID) {
          const questions = await getCandidateQuestionnaireQuestions(
            candidateQuestionnaireBase.candidateQuestionnaireID,
          );

          const questionsWithOptionsMap = new Map<
            number,
            { questionText: string; options: QuestionOption[] }
          >();

          const initialResponses: Record<string, string> = {};

          for (const question of questions) {
            const options = await getCandidateQuestionnaireQuestionOptions(
              question.questionID,
            );
            questionsWithOptionsMap.set(question.questionID, {
              questionText: question.questionText,
              options: options,
            });

            if (question.questionText.trim().endsWith("?")) {
              initialResponses[`${question.questionID}`] = "";
            }

            if (options.length > 0) {
              options.forEach((option) => {
                initialResponses[`${option.questionOptionID}`] = "";
              });
            }
          }

          setQuestionsWithOptions(questionsWithOptionsMap);
          setResponses(initialResponses);
        }

        const pageDecorationForm = await getAdditionalForm(
          clientResponse.clientID,
          5,
        );
        setPageDecorationForm(pageDecorationForm);

        if (pageDecorationForm) {
          const pageDecorationUrl = await HttpService.getFile(
            "page-decorations",
            clientResponse.clientID.toString(),
            pageDecorationForm.completedFormUrl,
          );
          // const cleanedPageDecorationDataUrl = await getCleanSignature(
          //   pageDecorationUrl
          // );
          setPageDecorationUrl(pageDecorationUrl);
        }
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadClientAndQuestions();
  }, [clientID, fiveYear]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleAuthorizationChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
    field: string,
  ) => {
    let value: string | boolean | Date =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;

    if (e.target.type === "date" && field === "dated") {
      value = new Date(e.target.value).toISOString();
    }

    setAuthorization((prev) => ({
      ...prev,
      [field]: value,
    }));
    setValidationErrors((prevErrors) => ({ ...prevErrors, [field]: "" }));
  };

  const handleCommentsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setComments(e.target.value);
    setValidationErrors((prevErrors) => ({ ...prevErrors, comments: "" }));
  };

  const handleResponseChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string,
  ) => {
    let value: string =
      e.target.type === "radio" ? e.target.value : e.target.value;

    setResponses((prev) => ({
      ...prev,
      [key]: value,
    }));
    setValidationErrors((prevErrors) => ({
      ...prevErrors,
      [key]: "",
      comments: "",
    }));
  };

  const validateFields = () => {
    const errors: { [key: string]: string } = {};
    setValidationErrors({});
    let hasYesAnswer = false;

    Object.keys(responses).forEach((key) => {
      const value = responses[key];
      if (!value) {
        errors.questions = "Please answer all questions.";
      }
      if (value === "Yes") {
        hasYesAnswer = true;
      }
    });
    if (hasYesAnswer && (comments === null || comments.trim() === "")) {
      errors.comments = "Please provide comments for 'Yes' responses.";
    }

    // Authorization Validation
    if (!authorization.checked)
      errors.checked = "You must agree before continuing.";
    if (!isValidDate(authorization.dated)) {
      errors.dated = "Date is required and must be valid.";
    }
    if (!authorization.fullName) errors.fullName = "Full name is required.";

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    setLoadingSpinner(true);
    try {
      if (!validateFields()) {
        console.log("Validation failed. Please fix the errors.");
        setLoadingSpinner(false);
        const firstErrorElement = document.querySelector(".invalid-feedback");
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
        return;
      }

      if (
        order &&
        candidateQuestionnaire &&
        authorization &&
        orderFormID &&
        questionsWithOptions
      ) {
        const result = await saveCandidateScreeningQuestionnaire(
          order,
          orderFormID,
          responses,
          questionsWithOptions,
          authorization,
          comments,
          candidateQuestionnaire,
          client?.diocese ? client.diocese : "",
          client?.diocese2 ? client.diocese2 : "",
          pageDecorationForm,
        );

        if (result) {
          navigate("/candidate/needs-review", {
            state: { successMessage: "Form successfully submitted." },
          });
        }
      } else {
        console.error("Please fill out required fields");
      }
    } catch (error) {
      console.error("Error saving form:", error);
      alert("Failed to save form. Please try again.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div>
      <div className="form-container" style={{ minHeight: "100vh" }}>
        {loading ? (
          <div className="loading-no-mask">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          <>
            <div className="questionnaire-header page-deco-container">
              {pageDecorationUrl ? (
                <div className="page-decoration">
                  <img
                    src={pageDecorationUrl}
                    alt="Logo"
                    style={{
                      maxWidth: "150px",
                      maxHeight: "150px",
                      height: "auto",
                    }}
                  />
                  {!isEditing && isReadOnly && (
                    <ToolTip
                      tooltipText={
                        isEditing
                          ? "Logo background gets removed when questionnaire is generated or viewed by candidate."
                          : "Upload a new logo on the Required Forms Page."
                      }
                      placement="top"
                      className="info-icon-spec"
                    />
                  )}
                </div>
              ) : (
                !isEditing &&
                isReadOnly && (
                  <div className="page-decoration">
                    <div className="text">
                      <b>
                        Upload a logo to be used here on the Required Forms
                        Page.
                      </b>
                    </div>
                  </div>
                )
              )}
              {client?.diocese2?.trim() && (
                <div style={{ fontWeight: "bold", textAlign: "center" }}>
                  {client.diocese2.toUpperCase()}
                </div>
              )}
              {client?.diocese && (
                <div style={{ fontWeight: "bold", textAlign: "center" }}>
                  {client.diocese.toUpperCase()}
                </div>
              )}
              <div style={{ fontSize: "1.5rem", marginTop: "5px" }}>
                Candidate Questionnaire
              </div>
            </div>
            <br></br>
            <div className="text-body">
              <br />
              {/* YesNoText Section*/}
              {candidateQuestionnaire && (
                <div
                  className={`editable-text-container ${
                    isEditing ? "hover-enabled" : ""
                  }`}
                  onMouseEnter={() => isEditing && setIsHovered("yesNoText")}
                  onMouseLeave={() => isEditing && setIsHovered(null)}
                >
                  <div
                    className={`editable-text ${
                      isEditing && isHovered === "yesNoText" ? "blurred" : ""
                    }`}
                  >
                    {candidateQuestionnaire.yesNoText?.trim() ? (
                      <b>
                        {" "}
                        <span
                          dangerouslySetInnerHTML={{
                            __html: candidateQuestionnaire.yesNoText,
                          }}
                        />
                      </b>
                    ) : isEditing ? (
                      <span className="empty-placeholder">
                        [Click to add text]
                      </span>
                    ) : (
                      "Please check either the “Yes” or “No” box for each question..."
                    )}
                  </div>

                  {isEditing && isHovered === "yesNoText" && (
                    <button
                      className="edit-icon"
                      onClick={() => {
                        setEditingBaseText({
                          key: "yesNoText" as keyof CandidateQuestionnaire,
                          text: candidateQuestionnaire.yesNoText || "",
                        });
                        setShowEditBaseText(true);
                      }}
                    >
                      {candidateQuestionnaire.yesNoText?.trim() ? (
                        <i className="bi bi-pencil"></i>
                      ) : (
                        <i className="bi bi-plus"></i>
                      )}
                    </button>
                  )}
                </div>
              )}
              <br />
              <br></br>
              {/* ThroughoutText Section*/}
              {candidateQuestionnaire && (
                <div
                  className={`editable-text-container ${
                    isEditing ? "hover-enabled" : ""
                  }`}
                  onMouseEnter={() =>
                    isEditing && setIsHovered("throughoutText")
                  }
                  onMouseLeave={() => isEditing && setIsHovered(null)}
                >
                  <div
                    className={`editable-text ${
                      isEditing && isHovered === "throughoutText"
                        ? "blurred"
                        : ""
                    }`}
                  >
                    {candidateQuestionnaire.throughoutText?.trim() ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: candidateQuestionnaire.throughoutText
                            ?.trim()
                            .replace(/\n/g, "<br />"),
                        }}
                      />
                    ) : isEditing ? (
                      <span className="empty-placeholder">
                        [Click to add text]
                      </span>
                    ) : (
                      "*Throughout this document, “charged” indicates allegations made in writing and known to you."
                    )}
                  </div>

                  {isEditing && isHovered === "throughoutText" && (
                    <button
                      className="edit-icon"
                      onClick={() => {
                        setEditingBaseText({
                          key: "throughoutText" as keyof CandidateQuestionnaire,
                          text: candidateQuestionnaire.throughoutText || "",
                        });
                        setShowEditBaseText(true);
                      }}
                    >
                      {candidateQuestionnaire.throughoutText?.trim() ? (
                        <i className="bi bi-pencil"></i>
                      ) : (
                        <i className="bi bi-plus"></i>
                      )}
                    </button>
                  )}
                </div>
              )}

              {/* Edit Modal for YesNoText & ThroughoutText */}
              {showEditBaseText &&
                editingBaseText !== null &&
                [
                  "yesNoText",
                  "throughoutText",
                  "statementOfApplicant",
                ].includes(editingBaseText.key) &&
                client && (
                  <EditBaseText
                    type={"candidate"}
                    clientID={client.clientID}
                    baseTextKey={
                      editingBaseText.key as keyof ReferenceQuestionnaire
                    }
                    baseText={editingBaseText.text}
                    onClose={(updatedText, successMessage) => {
                      setCandidateQuestionnaire((prev) =>
                        prev
                          ? {
                              ...prev,
                              [editingBaseText.key]: updatedText,
                            }
                          : prev,
                      );
                      if (successMessage) {
                        setSuccessMessage(successMessage);
                      }
                      setShowEditBaseText(false);
                    }}
                  />
                )}
              <br />
              {validationErrors.questions && (
                <div
                  className="invalid-feedback"
                  style={{ display: "block", fontSize: "large" }}
                >
                  {validationErrors.questions}
                </div>
              )}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 40px 40px",
                  gap: "1rem",
                  alignItems: "center",
                  background: "#fff",
                  position: "sticky",
                  top: 0,
                  zIndex: 10,
                  borderBottom: "1px solid #ccc",
                  padding: "0.5rem 0",
                }}
              >
                <div></div>
                <div style={{ textAlign: "center", fontWeight: "bold" }}>
                  No
                </div>
                <div style={{ textAlign: "center", fontWeight: "bold" }}>
                  Yes
                </div>
              </div>

              <ol style={{ listStyleType: "decimal", paddingTop: "20px" }}>
                {Array.from(questionsWithOptions.entries()).map(
                  ([questionID, { questionText, options }]) => {
                    const questionGridTemplate = isEditing
                      ? options.length > 0
                        ? "40px 1fr 40px 40px"
                        : "40px 1fr 40px 40px"
                      : options.length > 0
                        ? "1fr 40px 40px"
                        : "1fr 40px 40px";

                    const optionGridTemplate = isEditing
                      ? "40px 1fr 40px 40px"
                      : "1fr 40px 40px";

                    return (
                      <li key={questionID} style={{ marginBottom: "1rem" }}>
                        {/* Question Row */}
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: questionGridTemplate,
                            gap: "0.5rem",
                            alignItems: "center",
                          }}
                        >
                          {isEditing && (
                            <div>
                              <button
                                className="edit-icon-inline"
                                onClick={() => {
                                  setEditingItem({
                                    id: questionID,
                                    text: questionText,
                                    type: "question",
                                  });
                                  setShowEditModal(true);
                                }}
                              >
                                <i className="bi bi-pencil"></i>
                              </button>
                            </div>
                          )}
                          <span style={{ maxWidth: "90%" }}>
                            {questionText}
                          </span>
                          {questionText?.trim().endsWith("?") && (
                            <>
                              <label>
                                <input
                                  type="radio"
                                  name={`question-${questionID}`}
                                  onChange={() =>
                                    handleResponseChange(
                                      {
                                        target: {
                                          value: "No",
                                          type: "radio",
                                        },
                                      } as React.ChangeEvent<HTMLInputElement>,
                                      `${questionID}`,
                                    )
                                  }
                                  disabled={isReadOnly}
                                />
                              </label>
                              <label>
                                <input
                                  type="radio"
                                  name={`question-${questionID}`}
                                  onChange={() =>
                                    handleResponseChange(
                                      {
                                        target: {
                                          value: "Yes",
                                          type: "radio",
                                        },
                                      } as React.ChangeEvent<HTMLInputElement>,
                                      `${questionID}`,
                                    )
                                  }
                                  disabled={isReadOnly}
                                />
                              </label>
                            </>
                          )}
                        </div>

                        {options.length > 0 && (
                          <ol
                            style={{
                              marginLeft: "1.5rem",
                              listStyleType: "lower-alpha",
                            }}
                          >
                            {options.map((option) => (
                              <li key={option.questionOptionID}>
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: optionGridTemplate,
                                    gap: "0.5rem",
                                    alignItems: "center",
                                  }}
                                >
                                  {isEditing && (
                                    <div>
                                      <button
                                        className="edit-icon-inline"
                                        onClick={() => {
                                          setEditingItem({
                                            id: option.questionOptionID,
                                            text: option.questionOptionText,
                                            type: "option",
                                          });
                                          setShowEditModal(true);
                                        }}
                                      >
                                        <i className="bi bi-pencil"></i>
                                      </button>
                                    </div>
                                  )}
                                  <span>{option.questionOptionText}</span>
                                  <label>
                                    <input
                                      type="radio"
                                      name={`option-${option.questionOptionID}`}
                                      value="No"
                                      onChange={() =>
                                        handleResponseChange(
                                          {
                                            target: {
                                              type: "radio",
                                              value: "No",
                                            },
                                          } as React.ChangeEvent<HTMLInputElement>,
                                          `${option.questionOptionID}`,
                                        )
                                      }
                                      disabled={isReadOnly}
                                    />
                                  </label>
                                  <label>
                                    <input
                                      type="radio"
                                      name={`option-${option.questionOptionID}`}
                                      value="Yes"
                                      onChange={() =>
                                        handleResponseChange(
                                          {
                                            target: {
                                              type: "radio",
                                              value: "Yes",
                                            },
                                          } as React.ChangeEvent<HTMLInputElement>,
                                          `${option.questionOptionID}`,
                                        )
                                      }
                                      disabled={isReadOnly}
                                    />
                                  </label>
                                </div>
                              </li>
                            ))}
                          </ol>
                        )}
                      </li>
                    );
                  },
                )}
              </ol>
              {showEditModal && editingItem && (
                <EditQuestion
                  questionnaireType={"candidate"}
                  itemID={editingItem.id}
                  text={editingItem.text}
                  type={editingItem.type}
                  onClose={() => window.location.reload()}
                />
              )}

              {isEditing && (
                <div className="filter-actions">
                  <button
                    className={`oxbtn btn-new`}
                    onClick={() => {
                      setShowAddModal(true);
                    }}
                  >
                    <div> Add Question </div>
                    <i className="bi bi-plus-circle-fill plus-icon"></i>
                  </button>
                </div>
              )}

              {showAddModal && candidateQuestionnaire && (
                <AddQuestion
                  type={"candidate"}
                  questionnaireID={
                    candidateQuestionnaire.candidateQuestionnaireID
                  }
                  onClose={() => window.location.reload()}
                />
              )}

              <br></br>
              <div className="text-body">
                If you answered 'Yes' to any question, please list the question
                number(s), provide relevant details, and explain how the issue
                was resolved. You may also use the comments section below to
                provide any additional information or context.
              </div>
              <br></br>
              <div>Comments/Explanations</div>
              <div>
                <textarea
                  className="form-control"
                  value={comments}
                  disabled={isReadOnly}
                  onChange={(e) => handleCommentsChange(e)}
                  style={{ height: "100px" }}
                  maxLength={4000}
                />
                <div
                  style={{
                    fontSize: "14px",
                    marginTop: "4px",
                    textAlign: "right",
                    opacity: isReadOnly ? 0.6 : 1,
                  }}
                >
                  {comments?.length ?? 0}/4000
                </div>
              </div>
              {validationErrors.comments && (
                <div className="invalid-feedback" style={{ display: "block" }}>
                  {validationErrors.comments}
                </div>
              )}
              <br />

              <AuthorizationForm
                authText={candidateQuestionnaire?.statementOfApplicant}
                authorization={authorization}
                validationErrors={validationErrors}
                onInputChange={handleAuthorizationChange}
                type={""}
                isReadOnly={isReadOnly}
                isEditing={isEditing}
                onEditClick={(text) => {
                  setEditingBaseText({
                    key: "statementOfApplicant" as keyof CandidateQuestionnaire,
                    text: text,
                  });
                  setShowEditBaseText(true);
                }}
              ></AuthorizationForm>

              <br />
              {!isReadOnly && (
                <div className="btn-center">
                  <button
                    className="oxbtn btn-green"
                    onClick={() => handleSave()}
                  >
                    Submit
                  </button>
                </div>
              )}
              {loadingSpinner && (
                <div className="loading-mask">
                  <div className="loading-spinner"></div>
                </div>
              )}
            </div>
          </>
        )}
        {successMessage && (
          <div className="alert-message alert alert-success alert-dismissible fade show">
            {successMessage}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSuccessMessage("")}
              aria-label="Close"
            ></button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateForm;
